import { createFileRoute, Link } from "@tanstack/react-router"
import { ArrowUpRight, Check, Package } from "lucide-react"
import { useProducts } from "@/hooks/useProducts"
import { SectionSkeleton, ErrorState } from "@/components/state"
import { pageMeta } from "@/lib/seo"

export const Route = createFileRoute("/_public/products/")({
  component: Products,
  head: () =>
    pageMeta({
      title: "Products — Nexovia IT",
      description: "Products built and maintained by Nexovia IT.",
      path: "/products",
    }),
})

function Products() {
  const { data: products, isLoading, isError } = useProducts()
  const activeProducts = (products ?? []).filter((p) => p.is_active)

  return (
    <>
      {/* HERO */}
      <section className="relative overflow-hidden border-b border-border bg-background">
        <div
          className="absolute inset-0 -z-10 opacity-60"
          style={{ background: "radial-gradient(ellipse at top, color-mix(in oklab, var(--rust-glow) 22%, transparent), transparent 60%)" }}
        />
        <div className="mx-auto max-w-7xl px-6 py-24 md:py-28 text-center">
          <span className="inline-flex items-center gap-2 rounded-full border border-border bg-background px-4 py-1.5 text-xs font-medium uppercase tracking-widest text-rust">
            <span className="h-1.5 w-1.5 rounded-full bg-rust" /> Products
          </span>
          <h1 className="mx-auto mt-6 max-w-4xl text-5xl md:text-7xl font-bold text-balance">
            Products We <span className="text-rust">Build and Run.</span>
          </h1>
          <p className="mx-auto mt-6 max-w-2xl text-lg text-muted-foreground">
            Beyond client work, we build and maintain our own software — used by real customers, held to the
            same engineering standard we bring to every project.
          </p>
          <div className="mt-10 flex flex-wrap justify-center gap-3">
            <Link to="/contact" className="inline-flex items-center gap-2 rounded-full bg-gradient-accent px-6 py-3 text-sm font-semibold text-cream shadow-elegant">
              Talk to us <ArrowUpRight className="h-4 w-4" />
            </Link>
            <Link to="/services" className="inline-flex items-center gap-2 rounded-full border border-border px-6 py-3 text-sm font-semibold hover:bg-secondary">
              Explore our services
            </Link>
          </div>
        </div>
      </section>

      {/* PRODUCTS */}
      <section className="mx-auto max-w-7xl px-6 py-24 md:py-28">
        {isLoading ? (
          <SectionSkeleton rows={3} />
        ) : isError ? (
          <ErrorState message="Couldn't load products right now." />
        ) : activeProducts.length === 0 ? (
          <p className="text-center text-muted-foreground">No products published yet.</p>
        ) : (
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {activeProducts.map((p) => (
              <article
                key={p.id}
                className="group flex flex-col overflow-hidden rounded-2xl border border-border bg-card transition hover:-translate-y-1 hover:shadow-card"
              >
                <div className="relative aspect-[16/10] overflow-hidden bg-gradient-hero">
                  {p.image_url ? (
                    <img
                      src={p.image_url}
                      alt={p.title}
                      width={1600}
                      height={1000}
                      className="h-full w-full object-cover transition duration-500 group-hover:scale-105"
                    />
                  ) : (
                    <>
                      <div className="absolute inset-0 grid-noise opacity-40" />
                      <div className="absolute inset-0 flex items-center justify-center">
                        <Package className="h-10 w-10 text-cream/80" />
                      </div>
                    </>
                  )}
                  {p.price_label && (
                    <span className="absolute right-3 top-3 rounded-full bg-cream px-3 py-1 text-xs font-semibold text-ink shadow-elegant">
                      {p.price_label}
                    </span>
                  )}
                </div>
                <div className="flex flex-1 flex-col p-6">
                  {p.tagline && <span className="text-xs font-semibold uppercase tracking-widest text-rust">{p.tagline}</span>}
                  <h2 className="mt-2 text-xl font-bold">{p.title}</h2>
                  {p.description && <p className="mt-2 flex-1 text-sm text-muted-foreground leading-relaxed line-clamp-3">{p.description}</p>}
                  {p.features.length > 0 && (
                    <ul className="mt-4 space-y-1.5">
                      {p.features.slice(0, 3).map((feature) => (
                        <li key={feature} className="flex items-start gap-2 text-sm">
                          <Check className="mt-0.5 h-4 w-4 shrink-0 text-rust" />
                          <span>{feature}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                  <Link
                    to="/products/$slug"
                    params={{ slug: p.slug }}
                    className="mt-5 flex items-center justify-center gap-2 rounded-full border border-border px-5 py-2.5 text-sm font-semibold text-ink transition group-hover:border-rust group-hover:text-rust"
                  >
                    See Details <ArrowUpRight className="h-4 w-4" />
                  </Link>
                </div>
              </article>
            ))}
          </div>
        )}
      </section>

      {/* CTA */}
      <section className="border-t border-border bg-secondary/30">
        <div className="mx-auto max-w-5xl px-6 py-24 md:py-28 text-center">
          <p className="text-xs font-semibold uppercase tracking-widest text-rust">Something custom?</p>
          <h2 className="mt-3 text-4xl md:text-5xl font-bold">We Also Build Bespoke Products.</h2>
          <p className="mx-auto mt-4 max-w-xl text-muted-foreground">
            If none of these fit, we can design and build a product tailored to your business from the ground up.
          </p>
          <div className="mt-8 flex flex-wrap justify-center gap-3">
            <Link to="/contact" className="inline-flex items-center gap-2 rounded-full bg-gradient-accent px-6 py-3 text-sm font-semibold text-cream shadow-elegant">
              Start a project <ArrowUpRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>
    </>
  )
}
